import type { CompanyProfile, PurchaseOrder } from "@/api/types";
import { useTaxWords } from "@/app/session";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { PrintAmountInWords, PrintFooter, PrintHeader, PrintSignature } from "./DocumentSheet";

const NOT_FINAL: Partial<Record<PurchaseOrder["status"], string>> = {
  draft: "This is a draft and has not been sent.",
  cancelled: "This purchase order has been cancelled.",
};

/** A purchase order as printed: the invoice's layout, with who it's delivered to and when. */
export function PurchaseOrderPrint({
  order,
  seller,
}: {
  order: PurchaseOrder;
  seller: CompanyProfile | undefined;
}) {
  const say = useTaxWords();
  return (
    <article>
      <PrintHeader
        seller={seller}
        documentTitle={order.status === "draft" ? "Draft purchase order" : "Purchase order"}
        number={order.number}
        date={order.date}
        buyerLabel="Order to"
        buyerName={order.supplierName}
        buyerTaxId={order.supplierTaxId}
      />

      <p className="mb-3 text-sm">
        <span className="text-muted-foreground">Expected </span>
        {formatDate(order.expected)}
        {order.deliverTo ? (
          <>
            <span className="text-muted-foreground"> · Deliver to </span>
            {order.deliverTo}
          </>
        ) : null}
      </p>

      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">What is ordered</caption>
        <thead>
          <tr className="border-border border-b">
            <th scope="col" className="text-muted-foreground py-1.5 text-left text-xs font-medium">
              Description
            </th>
            <th scope="col" className="text-muted-foreground py-1.5 text-right text-xs font-medium">
              Qty
            </th>
            <th scope="col" className="text-muted-foreground py-1.5 text-right text-xs font-medium">
              Unit price
            </th>
            <th scope="col" className="text-muted-foreground py-1.5 text-right text-xs font-medium">
              {say("VAT")}
            </th>
            <th scope="col" className="text-muted-foreground py-1.5 text-right text-xs font-medium">
              Amount
            </th>
          </tr>
        </thead>
        <tbody>
          {order.lines.map((line) => (
            <tr key={line.id} className="border-muted border-b">
              <td className="text-foreground py-1.5">{line.description}</td>
              <td className="num text-muted-foreground py-1.5 text-right">{line.quantity}</td>
              <td className="num text-foreground py-1.5 text-right">
                {formatAmount(line.unitPrice)}
              </td>
              <td className="num text-muted-foreground py-1.5 text-right">{line.vatRatePct}%</td>
              <td className="num text-foreground py-1.5 text-right">
                {formatAmount(line.lineNet)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <dl className="ml-auto mt-4 w-64 text-sm">
        <Figure label="Net" value={order.subtotal} />
        <Figure label={say("VAT")} value={order.vat} />
        <Figure label="Total" value={order.total} emphasis />
      </dl>

      <PrintAmountInWords amount={order.total} currency={order.currency} />

      {order.terms ? (
        <section className="mt-4 text-sm">
          <h2 className="font-medium">Terms</h2>
          <p className="text-muted-foreground whitespace-pre-line">{order.terms}</p>
        </section>
      ) : null}

      <PrintSignature byLabel="Authorised by" />

      <PrintFooter note={NOT_FINAL[order.status] ?? order.note} />
    </article>
  );
}

/** What arrived, for the store's file. It repeats nothing else, because the order is the document. */
export function GoodsReceiptPrint({
  order,
  seller,
}: {
  order: PurchaseOrder;
  seller: CompanyProfile | undefined;
}) {
  return (
    <article>
      <PrintHeader
        seller={seller}
        documentTitle="Goods receipt note"
        number={order.number}
        date={order.receipts.at(-1)?.date ?? order.date}
        buyerLabel="From"
        buyerName={order.supplierName}
        buyerTaxId={order.supplierTaxId}
      />

      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">What was received</caption>
        <thead>
          <tr className="border-border border-b">
            <th scope="col" className="text-muted-foreground py-1.5 text-left text-xs font-medium">
              Description
            </th>
            <th scope="col" className="text-muted-foreground py-1.5 text-left text-xs font-medium">
              Received on
            </th>
            <th scope="col" className="text-muted-foreground py-1.5 text-right text-xs font-medium">
              Qty received
            </th>
          </tr>
        </thead>
        <tbody>
          {order.receipts.map((receipt, index) => (
            <tr key={`${receipt.lineId}-${index}`} className="border-muted border-b">
              <td className="text-foreground py-1.5">{receipt.description}</td>
              <td className="text-muted-foreground py-1.5">{formatDate(receipt.date)}</td>
              <td className="num text-foreground py-1.5 text-right">{receipt.quantity}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <PrintSignature byLabel="Received by" />

      <PrintFooter />
    </article>
  );
}

function Figure({
  label,
  value,
  emphasis = false,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div
      className={`flex items-baseline justify-between py-0.5 ${emphasis ? "border-border mt-1 border-t pt-1 font-semibold" : ""}`}
    >
      <dt className={emphasis ? "text-foreground" : "text-muted-foreground"}>{label}</dt>
      <dd className="num text-foreground">{formatAmount(value)}</dd>
    </div>
  );
}
